<template>
  <b-card
    no-body
  >
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>Mostrar</label>
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            @click="customerAddSidebarActive = true"
          >
            <span class="align-middle">Nuevo</span>
          </b-button>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              placeholder="Buscar..."
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <div class="mx-2 mb-2">
      <b-row>
        <!-- Pagination info -->
        <b-col
          v-if="customersMeta"
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">Mostrando del {{ customersMeta.from }} al {{ customersMeta.to }} de {{ customersMeta.total }} registros</span>

          <b-button
            v-if="customersData"
            variant="link"
            class="btn"
            :disabled="excelIsGenerating"
            @click="downloadExcelFile"
          >
            <b-spinner
              v-if="excelIsGenerating"
              small
            />
            <feather-icon
              v-else
              icon="DownloadIcon"
            />
            Descargar Excel
          </b-button>
        </b-col>
      </b-row>
    </div>

    <b-table
      class="position-relative"
      hover
      responsive
      small
      :busy="tableIsBusy"
      primary-key="id"
      show-empty
      empty-text="No se encontraron registros coincidientes"
      :items="customersData"
      :fields="customersFields"
      :per-page="perPage"
    >
      <template #table-busy>
        <div class="text-center text-dark my-2">
          <b-spinner class="align-middle" />
        </div>
      </template>

      <template #head(id)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <template #head(name)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <template #head(nit)="data">
        <span
          class="text-secondary pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <!-- Column: Id -->
      <template #cell(id)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="$emit('addCustomerTab', data.item.id)"
        >
          {{ data.item.id }}
        </b-link>
      </template>

      <!-- Column: Name -->
      <template #cell(name)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="$emit('addCustomerTab', data.item.id)"
        >
          {{ data.item.name }}
        </b-link>
      </template>

      <!-- Column: Origin -->
      <template #cell(origin)="data">
        <b-badge
          v-if="data.item.origin"
          variant="light-info"
        >
          {{ getOriginLabel(data.item.origin) }}
        </b-badge>
        <span
          v-else
          class="text-muted"
        >
          -
        </span>
      </template>

      <!-- Column: Customer Type -->
      <template #cell(customer_type)="data">
        <b-badge
          v-if="data.item.customer_type"
          :variant="getCustomerTypeVariant(data.item.customer_type)"
          pill
        >
          {{ getCustomerTypeLabel(data.item.customer_type) }}
        </b-badge>
        <span
          v-else
          class="text-muted"
        >
          -
        </span>
      </template>

      <!-- Column: Status -->
      <template #cell(status)="data">
        <b-badge :variant="`light-${ data.item.status === 'Y' ? 'success' : 'danger' }`">
          {{ data.item.status === 'Y' ? 'Activo' : 'Inactivo' }}
        </b-badge>
      </template>

      <!-- Column: Lista de precios -->
      <template #cell(price_list)="data">
        <b-badge
          v-if="data.item.price_list"
          :variant="data.item.price_list.is_default ? 'light-secondary' : 'light-warning'"
          :title="data.item.price_list.is_default
            ? 'Lista estándar de los clientes nuevos'
            : 'Asignación especial: no es la lista por defecto'"
        >
          {{ data.item.price_list.name }}
        </b-badge>
        <small
          v-else
          class="text-muted"
        >
          Sin lista
        </small>
      </template>

      <!-- Column: Ruta (solo para admin) -->
      <template
        v-if="isAdmin"
        #cell(route)="data"
      >
        <div v-if="data.item.route">
          <b-badge variant="light-primary">
            {{ data.item.route.name }}
          </b-badge>
          <small
            v-if="data.item.route.user && data.item.route.user.name"
            class="d-block text-muted"
          >
            {{ data.item.route.user.name }}
          </small>
          <small
            v-else
            class="d-block text-muted"
          >
            Sin vendedor
          </small>
        </div>
        <small
          v-else
          class="text-muted"
        >
          Sin ruta
        </small>
      </template>

    </b-table>

    <div class="mx-2 mb-2">
      <b-row>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-if="customersMeta"
            v-model="currentPage"
            :total-rows="customersMeta.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <!-- Customer Add Sidebar -->
    <customers-add
      :customer-add-sidebar-active.sync="customerAddSidebarActive"
      @refreshData="getCustomers"
    />
  </b-card>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import { isAdmin } from '@/utils/auth'
import Ripple from 'vue-ripple-directive'
import * as XLSX from 'xlsx'
import CustomersAdd from './CustomersAdd.vue'

export default {
  components: {
    vSelect,
    CustomersAdd,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      searchQuery: '',
      sortField: 'id',
      sortDesc: 'desc',
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      currentPage: 1,
      searchQueryTimeout: null,
      isAdmin: false,
      customerAddSidebarActive: false,
      excelIsGenerating: false,

      customersFields: [
        { key: 'id', label: '#' },
        { key: 'name', label: 'Cliente' },
        { key: 'nit', label: 'NIT' },
        { key: 'cellphone', label: 'Celular' },
        { key: 'email', label: 'Correo' },
        { key: 'origin', label: 'Origen' },
        { key: 'customer_type', label: 'Tipo' },
        { key: 'price_list', label: 'Lista de precios' },
        { key: 'status', label: 'Estado' },
        // Campo condicional para administradores
        ...(isAdmin() ? [{ key: 'route', label: 'Ruta' }] : []),
        { key: 'created_at', label: 'Creado' },
      ],
      customersData: null,
      customersMeta: null,
      tableIsBusy: true,
    }
  },

  computed: {
    excelFields() {
      const fields = {
        '#': 'id',
        Cliente: 'name',
        NIT: 'nit',
        'Razón social': 'nit_name',
        Celular: 'cellphone',
        Teléfono: 'phone',
        Correo: 'email',
        Dirección: { field: 'default_address', callback: (value, item) => (value && value.address) || item.address || '' },
        Departamento: { field: 'default_address', callback: value => (value && value.department) || '' },
        Municipio: { field: 'default_address', callback: value => (value && value.municipality) || '' },
        Origen: { field: 'origin', callback: value => (value ? this.getOriginLabel(value) : '') },
        'Tipo cliente': { field: 'customer_type', callback: value => (value ? this.getCustomerTypeLabel(value) : '') },
        Estado: { field: 'status', callback: value => (value === 'Y' ? 'Activo' : 'Inactivo') },
        'Lista de precios': { field: 'price_list', callback: value => (value ? value.name : '') },
      }

      if (this.isAdmin) {
        fields.Ruta = { field: 'route', callback: value => (value ? value.name : 'Sin ruta') }
        fields.Vendedor = { field: 'route', callback: value => (value && value.user ? value.user.name : '') }
      }

      fields['Fecha creación'] = 'created_at'

      return fields
    },
  },

  watch: {
    perPage() {
      this.tableIsBusy = true
      this.getCustomers()
    },
    currentPage() {
      this.tableIsBusy = true
      this.getCustomers()
    },
    searchQuery() {
      // Limpiar el timeout anterior
      clearTimeout(this.searchQueryTimeout)

      if (!this.searchQuery || this.searchQuery.trim() === '') {
        // Si esta vacío, buscar inmediatamente
        this.currentPage = 1
        this.tableIsBusy = true
        this.getCustomers()
      } else {
        // si hay texto, esperar 300ms para no saturar el servidor
        this.searchQueryTimeout = setTimeout(() => {
          this.currentPage = 1
          this.tableIsBusy = true
          this.getCustomers()
        }, 300)
      }
    },
  },

  created() {
    this.isAdmin = isAdmin()
    this.getCustomers()
  },

  methods: {
    getCurrentDate() {
      const date = new Date()
      return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`
    },

    getMonthName(month) {
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
      return months[Number(month) - 1] || ''
    },

    async fetchAllCustomers() {
      const total = this.customersMeta ? this.customersMeta.total : 0
      const response = await axios.get(`customer?perPage=${total || 1}&page=1&query=${this.searchQuery}&sortField=${this.sortField}&sortDesc=${this.sortDesc}`)
      return response.data.data
    },

    async downloadExcelFile() {
      this.excelIsGenerating = true
      try {
        const [customers, salesResponse] = await Promise.all([
          this.fetchAllCustomers(),
          this.isAdmin ? axios.get('customer-sales-by-route') : Promise.resolve(null),
        ])

        const customerRows = customers.map(item => {
          const row = {}
          Object.entries(this.excelFields).forEach(([label, def]) => {
            if (typeof def === 'string') {
              row[label] = item[def] != null ? item[def] : ''
            } else {
              row[label] = def.callback(item[def.field], item)
            }
          })
          return row
        })

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(customerRows), 'Clientes')

        if (salesResponse) {
          const salesData = salesResponse.data.data

          // Una columna de total por cada moneda presente en los datos (Total Q, Total $, ...)
          const currencies = salesData
            .map(item => ({ code: item.currency_code, symbol: item.currency_symbol }))
            .filter((currency, index, list) => list.findIndex(c => c.code === currency.code) === index)
            .sort((a, b) => a.code.localeCompare(b.code))

          const rowsMap = new Map()
          salesData.forEach(item => {
            const key = [item.sale_year, item.sale_month, item.route_name, item.customer_id, item.order_vendor_name].join('|')
            if (!rowsMap.has(key)) {
              const row = {
                Año: Number(item.sale_year),
                Mes: this.getMonthName(item.sale_month),
                Ruta: item.route_name,
                Cliente: item.customer_name,
                'Vendedor pedido': item.order_vendor_name,
                'Vendedor actual': item.current_vendor_name,
                'Cambio vendedor': item.vendor_changed,
                Pedidos: 0,
                'Cantidad (unidades vendidas)': 0,
              }
              currencies.forEach(currency => {
                row[`Total ${currency.symbol}`] = 0
              })
              rowsMap.set(key, row)
            }
            const row = rowsMap.get(key)
            row.Pedidos += Number(item.orders_count)
            row['Cantidad (unidades vendidas)'] += Number(item.total_quantity)
            row[`Total ${item.currency_symbol}`] += Number(item.total_amount)
          })

          XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet([...rowsMap.values()]), 'Ventas despachadas')
        }

        XLSX.writeFile(workbook, `clientes_${this.getCurrentDate()}.xlsx`)
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.excelIsGenerating = false
      }
    },

    getCustomers() {
      if (this.perPage === 'Todos') {
        this.perPage = this.customersMeta.total
      }

      axios
        .get(`customer?perPage=${this.perPage}&page=${this.currentPage}&query=${this.searchQuery}&sortField=${this.sortField}&sortDesc=${this.sortDesc}`)
        .then(response => {
          this.tableIsBusy = false
          this.customersMeta = response.data.meta
          this.customersData = response.data.data
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    deleteCustomer(id) {
      this.$bvModal
        .msgBoxConfirm('¿Deseas eliminar el cliente?', {
          title: 'Eliminar cliente',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'SI',
          cancelTitle: 'NO',
          okTitleVariant: 'outline-danger',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            axios
              .delete(`customer/${id}`)
              .then(response => {
                this.makeToast('success', 'Cliente eliminado', `Se ha eliminado el cliente ${response.data.data.name}.`)
                this.getCustomers()
              })
              .catch(error => {
                this.showErrors(error)
              })
          }
        })
    },

    sortColumn(field) {
      if (this.sortField === field) {
        if (this.sortDesc === 'asc') {
          this.sortDesc = 'desc'
        } else {
          this.sortDesc = 'asc'
        }
      } else {
        this.sortField = field
        this.sortDesc = 'asc'
      }
      this.getCustomers()
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },

    getOriginLabel(origin) {
      const originLabels = {
        pagina_web: 'Página Web',
        redes_sociales: 'Redes Sociales',
        referido: 'Referido',
        evento: 'Evento',
        publicidad: 'Publicidad',
        otros: 'Otros',
      }
      return originLabels[origin] || origin
    },

    getCustomerTypeLabel(type) {
      const map = {
        cliente_final: 'Cliente final',
        cliente_online: 'Cliente Online',
        excedentes: 'Excedentes',
      }
      return map[type] || type
    },

    getCustomerTypeVariant(type) {
      const variants = {
        cliente_final: 'light-success',
        cliente_online: 'light-info',
        excedentes: 'light-warning',
      }
      return variants[type] || 'light-secondary'
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.pointer {
  cursor: pointer;
}

::v-deep .badge-light-primary {
  background-color: #838383 !important;
  color: white !important;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
